import { CommonModule } from '@angular/common';
import { Component, OnInit, AfterViewInit, ViewChildren, QueryList, ElementRef, ViewEncapsulation } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { CanonicalService } from '../../../shared/seo/canonical.service';

interface Card {
  title: string;
  description: string;
}

interface Card4 {
  icon: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-software-consulting',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, HttpClientModule],
  templateUrl: './software-consulting.component.html',
  styleUrls: ['./software-consulting.component.scss'],
  encapsulation: ViewEncapsulation.None 
})
export class SoftwareConsultingComponent implements OnInit {

  constructor(private titleService: Title, private metaService: Meta,private canonicalService: CanonicalService) {}

  ngOnInit(): void {
    // ✅ Set page title
    this.titleService.setTitle('Software Consulting | Expert IT Advice by MALtech');

    // ✅ Set meta description
    this.metaService.updateTag({
      name: 'description',
      content: 'Expert software consulting to unlock your business potential. Services include cloud migration, DevOps automation, and custom software solutions.'
    });

    // Set meta keywords
    this.metaService.updateTag({
      name: 'keywords',
      content: 'it, software, consulting, services'
    });

    // ✅ Set robots meta tag
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });

    this.canonicalService.setCanonicalURL('service/software-consulting-services/');

    // ✅ Load animation steps sequentially
    this.loadStepsSequentially();
  }

  getStart() {
    const plansSection = document.getElementById('get-start');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // ---------------------------
  // SECTION 1: Service Cards
  // ---------------------------
  cards2 = [
    { icon: 'assets/images/consulting/cloud.webp', title: 'Cloud & DevOps Consulting', description: 'Expert guidance on cloud adoption, DevOps practices, and infrastructure optimization.' },
    { icon: 'assets/images/consulting/scale.webp', title: 'Software Architecture & System Design', description: 'Designing scalable, robust, and efficient software architectures tailored to your business needs.' },
    { icon: 'assets/images/consulting/graph.webp', title: 'Digital Transformation & Modernization', description: 'Modernizing legacy systems, embracing digital technologies, and driving business agility.' },
    { icon: 'assets/images/consulting/db.webp', title: 'AI & Data Consulting', description: 'Leveraging AI and data analytics to gain insights, automate processes, and enhance decision-making.' },
    { icon: 'assets/images/consulting/sec.webp', title: 'Cybersecurity Consulting', description: 'Protecting your digital assets, ensuring data privacy, and mitigating cybersecurity risks.' },
    { icon: 'assets/images/consulting/chess.webp', title: 'Technology Strategy & Advisory', description: 'Strategic technology planning, IT roadmap development, and advisory services to align technology with business goals.' }
  ];

  // ---------------------------
  // SECTION 2: Process Steps
  // ---------------------------
  steps = [
    { icon: 'bi bi-search', title: 'Assessment & Discovery', description: 'Thoroughly analyzing your IT landscape, business objectives, and challenges.' },
    { icon: 'bi bi-flag', title: 'Strategic Roadmapping', description: 'Developing a clear, actionable roadmap aligned with your strategic goals and priorities.' },
    { icon: 'bi bi-pencil', title: 'Solution Design', description: 'Crafting detailed solution designs, including architecture, technology stack, and implementation plan.' },
    { icon: 'bi bi-gear', title: 'Execution Support', description: 'Providing hands-on technical and project support to ensure seamless execution and integration.' },
    { icon: 'bi bi-tv', title: 'Ongoing Advisory', description: 'Offering continuous guidance, optimization, and adaptation to evolving business needs.' }
  ];

  displayedSteps: any[] = [];

  loadStepsSequentially() {
    this.steps.forEach((step, index) => {
      setTimeout(() => {
        this.displayedSteps.push(step);
      }, index * 500); // Delay between each step
    });
  }

  // ---------------------------
  // SECTION 3: Cloud Services
  // ---------------------------
  section3Title: string = 'Cloud, DevOps & Automation Consulting Services';

  cards3: Card[] = [
    { title: 'Cloud Migration & Management', description: 'Seamless migration of applications, data, and workloads to AWS, Azure, or Google Cloud. Achieve minimal disruption and maximum scalability.' },
    { title: 'Cloud Optimization', description: 'Tailored SaaS, PaaS and IaaS solutions that enhance efficiency and align with your unique business needs.' },
    { title: 'Cloud Security', description: 'Advanced cloud security solutions including encryption, compliance checks, and proactive threat monitoring.' },
    { title: 'CI/CD Pipeline Automation', description: 'Accelerate software delivery with fully automated CI/CD pipelines that ensure speed, reliability, and consistency.' },
    { title: 'Infrastructure as Code (IaC)', description: 'Automating infrastructure setup and management with Terraform, Ansible, and CloudFormation for efficiency and reliability.' },
    { title: 'Containerization & Orchestration', description: 'Building scalable, portable applications with Docker and Kubernetes for smooth orchestration and efficiency.' }
  ];

  // ---------------------------
  // SECTION 4: Why Choose MALtech
  // ---------------------------
  section4Title = 'Why Choose MALtech?';
  
  cards4: Card4[] = [
    { icon: 'bi bi-globe-americas', title: 'Global Expertise', description: 'Our seasoned consultants bring deep expertise across industries and global markets.' },
    { icon: 'bi bi-puzzle', title: 'End-to-End Solutions', description: 'We offer comprehensive consulting services, from strategy to execution, ensuring a holistic approach.' },
    { icon: 'bi bi-rocket-takeoff', title: 'Innovation-Driven', description: 'We stay at the forefront of technology trends, leveraging cutting-edge solutions to drive innovation.' },
    { icon: 'bi bi-people', title: 'People-Centric Approach', description: 'We prioritize collaboration, understanding your unique needs, and building long-term partnerships.' }
  ];

  // ---------------------------
  // SECTION 5: CTA (Call to Action)
  // ---------------------------
  title = 'Ready to simplify complexity and accelerate growth?';
  description = 'Partner with MALtech today—your trusted consulting partner for lasting impact.';
  buttonText = "Let's Get Started";

  // ---------------------------
  // Scroll and Animation Logic
  // ---------------------------
  scrollToSection(event: Event, sectionId: string) {
    event.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
